import React, { useState } from 'react';
import styled from 'styled-components';

import { Star, Volume2 } from 'lucide-react';
import { useSmartTTS } from '../hooks/useSmartTTS';
import apiService from '../services/apiService';

const StyledButton = styled.button`
  background: #e50914;
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 18px;
  box-shadow: 0 4px 12px rgba(229,9,20,0.3);
  transition: all 0.2s;
  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    background: #333;
    color: #888;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
`;

const PopupContent = styled.div`
  background: #1f1f1f;
  border: 1px solid #333;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 420px;
  max-height: 85vh;
  overflow-y: auto;
  box-sizing: border-box;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  word-break: break-word;
  overflow-wrap: break-word;
  color: #e5e5e5;
  display: flex;
  flex-direction: column;
`;

const SectionLabel = styled.strong`
  color: #a3a3a3;
  font-size: 0.9rem;
  display: inline-block;
  margin-right: 8px;
`;

const WordChip = styled.span`
  display: inline-block;
  background: ${props => props.$active ? '#e50914' : '#2a2a2a'};
  color: ${props => props.$active ? '#fff' : '#e0e0e0'};
  border: 1px solid ${props => props.$active ? '#e50914' : '#444'};
  margin: 0 4px 6px 0;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#d40812' : '#3a3a3a'};
    transform: translateY(-1px);
  }
`;

const renderHighlightedText = (text) => {
    if (!text) return null;
    const parts = text.split(/\[\[(.*?)\]\]/g);
    return parts.map((part, i) => {
        if (i % 2 === 1) { // Captured group (inside [[ ]])
            return <span key={i} style={{ color: '#bae6fd', fontWeight: 'bold' }}>{part}</span>;
        }
        return part;
    });
};

function MeaningPopup({ source, target, onClose, wordInfo, onWordClick, loadingWord, onUpdateWordInfo }) {
    const [selectedWord, setSelectedWord] = useState(null);
    const { speak } = useSmartTTS();

    const handleWordClick = async (w) => {
        setSelectedWord(w);
        if (onWordClick) {
            await onWordClick(w);
        }
    };

    const handleSpeak = (text) => {
        if (text) {
            speak({ text, rate: 0.9, pitch: 1 });
        }
    };

    const [isFavoriteLocal, setIsFavoriteLocal] = useState(false);

    // Sync local state when wordInfo changes
    React.useEffect(() => {
        if (wordInfo) {
            setIsFavoriteLocal(!!wordInfo.isFavorite);
        }
    }, [wordInfo]);

    const toggleFavorite = async () => {
        const storedUser = localStorage.getItem("user");
        const user = storedUser ? JSON.parse(storedUser) : null;

        if (!user) {
            alert("로그인이 필요한 기능입니다.");
            return;
        }

        // Word ID가 없으면 먼저 저장하여 ID를 생성해야 함 (새로운 단어)
        if (!wordInfo || !wordInfo.id) {
            try {
                console.log("[MeaningPopup] Auto-saving word to History before favoriting...");
                const userId = user.id || user.uid || user.email;

                // wordInfo 구조: { word, meanings: [...], ... }
                // saveWordHistory 호출
                const saveResponse = await apiService.saveWordHistory(userId, wordInfo);

                if (saveResponse && saveResponse.id) {
                    const newId = saveResponse.id;
                    console.log("[MeaningPopup] Word auto-saved. New ID:", newId);

                    // 즐겨찾기 설정 (word.js에서 isFavorite=false로 초기화되므로 명시적 업데이트 필요)
                    await apiService.saveFavoriteWord(newId, true);

                    setIsFavoriteLocal(true);

                    if (onUpdateWordInfo) {
                        onUpdateWordInfo({ ...wordInfo, id: newId, isFavorite: true });
                    }
                }
            } catch (err) {
                console.error("Auto-save & Favorite failed:", err);
                alert("단어 저장에 실패했습니다.");
            }
            return;
        }

        // 기존 단어 토글 로직
        const newStatus = !isFavoriteLocal;
        setIsFavoriteLocal(newStatus);

        if (onUpdateWordInfo) {
            onUpdateWordInfo({ ...wordInfo, isFavorite: newStatus });
        }

        try {
            await apiService.saveFavoriteWord(wordInfo.id, newStatus);
        } catch (err) {
            console.error('Failed to toggle favorite', err);
            setIsFavoriteLocal(!newStatus);
            if (onUpdateWordInfo) {
                onUpdateWordInfo({ ...wordInfo, isFavorite: !newStatus });
            }
        }
    };

    return (
        <PopupOverlay onClick={onClose}>
            <PopupContent onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem' }}>의미 단위 매칭</h3>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <SectionLabel>원문</SectionLabel>
                        <button
                            onClick={() => handleSpeak(source)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 4 }}
                            title="전체 듣기"
                        >
                            <Volume2 size={16} />
                        </button>
                    </div>
                    <div style={{ lineHeight: 1.6 }}>
                        {source.split(/\s+/).map((w, i) => (
                            <WordChip
                                key={i}
                                $active={selectedWord === w}
                                onClick={() => handleWordClick(w)}
                                title={w}
                            >
                                {w}
                            </WordChip>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom: 20, padding: '12px', background: '#2a2a2a', borderRadius: '8px', borderLeft: '3px solid #e50914' }}>
                    <div style={{ color: '#a3a3a3', fontSize: '0.85rem', marginBottom: 4 }}>번역</div>
                    <div style={{ color: '#fff', fontSize: '1.05rem', lineHeight: 1.5 }}>
                        {target}
                    </div>
                </div>

                {loadingWord && (
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                        <div className="loader" style={{
                            display: 'inline-block',
                            width: 28,
                            height: 28,
                            border: '3px solid rgba(255,255,255,0.1)',
                            borderTop: '3px solid #e50914',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <div style={{ marginTop: 12, color: '#888', fontSize: '0.9rem' }}>
                            단어 분석 중...
                        </div>
                        <style>
                            {`@keyframes spin { 100% { transform: rotate(360deg); } }`}
                        </style>
                    </div>
                )}

                {wordInfo && wordInfo.word && !loadingWord && (
                    <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #333' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', wordBreak: 'normal' }}>{wordInfo.word}</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                    onClick={() => handleSpeak(wordInfo.word)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '8px' }}
                                >
                                    <Volume2 size={20} />
                                </button>
                                <button
                                    onClick={toggleFavorite}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: isFavoriteLocal ? '#e50914' : '#555', padding: '8px' }}
                                >
                                    <Star size={20} fill={isFavoriteLocal ? "#e50914" : "none"} />
                                </button>
                            </div>
                        </div>

                        <div>
                            {wordInfo.meanings.length > 0 ? (
                                wordInfo.meanings.map((m, idx) => (
                                    <div key={idx} style={{ marginBottom: 12, padding: '12px', background: '#262626', borderRadius: 8, border: '1px solid #333' }}>
                                        <div style={{ color: '#e5e5e5', marginBottom: 6 }}>
                                            <span style={{ color: '#e50914', fontSize: '0.9rem', marginRight: 6 }}>●</span>
                                            {m.의미1 || m.의미2 || m.의미3 || m.meaning || m.definition}
                                        </div>
                                        <div style={{ color: '#999', fontSize: '0.9rem', paddingLeft: 18 }}>
                                            {renderHighlightedText(m.예문1 || m.예문2 || m.예문3 || m.example || m.exampleSentence)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#666' }}>상세 분석 정보가 없습니다.</div>
                            )}
                        </div>
                    </div>
                )}

                <StyledButton style={{ marginTop: 24, width: '100%' }} onClick={onClose}>닫기</StyledButton>
            </PopupContent>
        </PopupOverlay>
    );
}

export default MeaningPopup;
